import {
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import DownArrow from '@mui/icons-material/ExpandMoreRounded';
import InputWrapper from '../InputWrapper';
import { important, isNil, sxToObject } from '../../../../utils/global/helper';
import { getInputBorderStyle } from '../../../../helpers/styles';
import Badge, { ColorScheme } from '../../../global/Badge';

interface Props {
  id: string;
  label?: string;
  isLabelMultiline?: boolean;
  placeholder?: string;
  disabled?: boolean;
  options: {
    value: string | boolean | number | null;
    label: string;
    hidden?: boolean;
    disabled?: boolean;
    badge?: {
      text: string;
      colorScheme: ColorScheme;
    };
  }[];
  tooltip?: string;
  autoWidth?: boolean;
  value: string | undefined;
  onChange: (value: string) => void;
  onBlur?: () => void;
  selectSx?: SxProps<Theme>;
  isError?: boolean;
  required?: boolean;
}

function SingleSelectControlled({
  id,
  label,
  placeholder,
  isLabelMultiline,
  disabled,
  options,
  tooltip,
  autoWidth = true,
  value,
  onChange,
  onBlur,
  selectSx,
  isError,
  required,
}: Props) {
  const theme = useTheme();
  const safeValue =
    isNil(value) || !options.some(o => String(o.value) === String(value))
      ? ''
      : value;
  return (
    <InputWrapper
      label={label}
      name={id.toString()}
      tooltip={tooltip}
      isLabelMultiline={isLabelMultiline}
      required={required}>
      <Select
        IconComponent={DownArrow}
        sx={{
          height: important('44px'),
          fontSize: 'body2.fontSize',
          '& .MuiSelect-select': {
            overflow: 'hidden',
          },
          '& .MuiOutlinedInput-notchedOutline': getInputBorderStyle({
            theme,
            isError,
          }),
          bgcolor: theme.palette.background.default,
          ...sxToObject(selectSx, theme),
        }}
        defaultValue={safeValue}
        value={safeValue}
        onChange={async (event: SelectChangeEvent) => {
          onChange(event.target.value as string);
        }}
        input={<OutlinedInput />}
        displayEmpty
        renderValue={val => {
          const foundLabel = options.find(option => option.value === val)
            ?.label;
          const isPlaceholder = !foundLabel;
          return (
            <Typography
              component={isPlaceholder ? 'em' : 'p'}
              variant="body2"
              noWrap
              color={
                isPlaceholder ? theme.palette.outlineGrey.main : undefined
              }>
              {foundLabel || placeholder}
            </Typography>
          );
        }}
        autoWidth={autoWidth}
        label={placeholder}
        disabled={disabled}
        onBlur={onBlur}>
        {placeholder && (
          <MenuItem sx={{ fontSize: 'body2.fontSize' }} disabled value="">
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {options.map(option => (
          <MenuItem
            sx={{
              fontSize: 'body2.fontSize',
              display: option.hidden ? 'none' : 'block',
            }}
            value={option.value as string}
            key={option.value?.toString() ?? 'null'}
            disabled={option.disabled}>
            {option.label}
            {option.badge && ' '}
            {option.badge && (
              <Badge
                text={option.badge.text}
                colorScheme={option.badge.colorScheme}
                rounded
                sx={{
                  display: 'inline-block',
                  width: 'fit-content',
                  borderStyle: 'none',
                  p: '3px 6px',
                }}
              />
            )}
          </MenuItem>
        ))}
      </Select>
    </InputWrapper>
  );
}

export default SingleSelectControlled;
